import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { fmtTotals, statusMeta } from "@/lib/format";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, Cell,
} from "recharts";
import { CurrencyDollar, Wrench, Users, Warning } from "@phosphor-icons/react";

function Kpi({ icon: Icon, label, value, accent }) {
  const isMulti = value && typeof value === "object";
  const entries = isMulti ? Object.entries(value).filter(([, v]) => v) : [];
  return (
    <Card className="p-6" data-testid={`kpi-${label}`}>
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <div className="text-sm text-muted-foreground">{label}</div>
          {isMulti ? (
            entries.length === 0 ? (
              <div className="font-head font-black text-2xl lg:text-3xl mt-2">0 ₴</div>
            ) : (
              <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
                {entries.map(([c, v]) => (
                  <div key={c} className="font-head font-black text-xl lg:text-2xl leading-tight">
                    {fmtTotals({ [c]: v })}
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className="font-head font-black text-2xl lg:text-3xl mt-2">{value}</div>
          )}
        </div>
        <div className="w-11 h-11 rounded-md flex items-center justify-center shrink-0" style={{ background: accent + "22" }}>
          <Icon size={22} weight="fill" style={{ color: accent }} />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  const navigate = useNavigate();

  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)); }, []);

  if (!d) return <div className="text-muted-foreground">Завантаження...</div>;

  return (
    <div>
      <PageHeader title="Дашборд" subtitle="Ключові показники автосервісу" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <Kpi icon={CurrencyDollar} label="Виручка за місяць" value={d.revenue_month_by_currency || {}} accent="#34C759" />
        <Kpi icon={Wrench} label="Активні наряди" value={d.active_orders} accent="#007AFF" />
        <Kpi icon={Users} label="Клієнти" value={d.clients_total} accent="#32ADE6" />
        <Kpi icon={Warning} label="Мало на складі" value={d.low_stock} accent="#FFCC00" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-head font-bold text-lg">Виручка (14 днів)</h3>
            <div className="flex items-center gap-4 text-xs">
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block" style={{ background: "#007AFF" }} />₴ ГРН</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block" style={{ background: "#34C759" }} />$ USD</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 inline-block" style={{ background: "#FF9500" }} />€ EUR</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={d.revenue_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="date" stroke="#71717a" fontSize={12} />
              <YAxis stroke="#71717a" fontSize={12} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, color: "#111" }} />
              <Line type="monotone" dataKey="UAH" name="₴ ГРН" stroke="#007AFF" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="USD" name="$ USD" stroke="#34C759" strokeWidth={2.5} dot={false} />
              <Line type="monotone" dataKey="EUR" name="€ EUR" stroke="#FF9500" strokeWidth={2.5} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <h3 className="font-head font-bold text-lg mb-4">Наряди за статусами</h3>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={d.orders_by_status}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
              <XAxis dataKey="status" stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={12} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 8, color: "#111" }} cursor={{ fill: "#00000008" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {d.orders_by_status.map((s) => <Cell key={s.key} fill={statusMeta(s.key).color} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="p-6">
          <h3 className="font-head font-bold text-lg mb-4">Завантаження майстрів</h3>
          <div className="space-y-3">
            {d.master_workload.length === 0 && <div className="text-sm text-muted-foreground">Немає активних нарядів</div>}
            {d.master_workload.map((m) => (
              <div key={m.master} className="flex items-center justify-between text-sm">
                <span>{m.master}</span>
                <span className="font-semibold px-2 py-0.5 rounded bg-secondary">{m.count} наряд(и)</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-head font-bold text-lg mb-4">Топ послуги</h3>
          <div className="space-y-3">
            {d.top_services.map((s, i) => (
              <div key={i} className="flex items-center justify-between text-sm">
                <span className="truncate mr-2">{s.name}</span>
                <span className="font-semibold text-primary">{s.count}×</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="p-6">
          <h3 className="font-head font-bold text-lg mb-4">Останні наряди</h3>
          <div className="space-y-2">
            {d.recent_orders.map((o) => (
              <button key={o.id} onClick={() => navigate(`/orders/${o.id}`)}
                data-testid={`recent-order-${o.number}`}
                className="w-full flex items-center justify-between text-sm py-2 border-b border-border last:border-0 hover:bg-accent/40 px-2 -mx-2 rounded transition-colors">
                <div className="text-left">
                  <div className="font-semibold">{o.number}</div>
                  <div className="text-xs text-muted-foreground">{o.client_name}</div>
                </div>
                <div className="text-right">
                  <div className="font-semibold">{fmtTotals(o.totals_by_currency)}</div>
                  <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: statusMeta(o.status).color + "22", color: statusMeta(o.status).color }}>
                    {o.status_label}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
